'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { useStore } from '@/lib/store';
import { Header } from '@/components/Header';
import { SettingsSection, SettingItem } from '@/components/SettingsSection';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  User,
  Lock,
  Shield,
  Bell,
  Users,
  MessageSquare,
  AlertTriangle,
  Save,
  Download,
  Link as LinkIcon,
  Trash2,
  Smartphone,
} from 'lucide-react';
import { toast } from 'sonner';
import { UserProfile } from '@/types/profile';
import { validateUsername } from '@/lib/usernameUtils';
import { usePWAInstall } from '@/hooks/usePWAInstall';
import { ProfilePictureUpload } from '@/components/ProfilePictureUpload';

export default function SettingsPage() {
  const { user, userProfile, updateProfile, linkGoogleAccount, loading: authLoading } = useAuth();
  const [sendingPasswordReset, setSendingPasswordReset] = useState(false);
  const [linkingGoogle, setLinkingGoogle] = useState(false);
  const { watchlist } = useStore();
  const router = useRouter();
  const { isInstallable, isIOS, isStandalone, triggerInstall, clearDismissedFlag } = usePWAInstall();

  // Form states
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [bio, setBio] = useState('');
  const [discordUsername, setDiscordUsername] = useState('');
  const [saving, setSaving] = useState(false);
  const [usernameError, setUsernameError] = useState('');
  const [usernameSuggestions, setUsernameSuggestions] = useState<string[]>([]);
  const [validatingUsername, setValidatingUsername] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) {
      router.push('/auth/signin');
    }
  }, [user, authLoading, router]);

  useEffect(() => {
    if (userProfile) {
      setUsername(userProfile.username || '');
      setDisplayName(userProfile.displayName || '');
      setBio(userProfile.bio || '');
      setDiscordUsername(userProfile.discordUsername || '');
    }
  }, [userProfile]);

  if (authLoading || !user || !userProfile) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-pulse text-white">Loading...</div>
      </div>
    );
  }

  const handleUsernameChange = async (newUsername: string) => {
    setUsername(newUsername);
    setUsernameError('');
    setUsernameSuggestions([]);

    // Don't validate if it's the same as current username
    if (newUsername === userProfile?.username) {
      return;
    }

    // Only validate if username has content
    if (newUsername.length > 0) {
      setValidatingUsername(true);
      try {
        const validation = await validateUsername(newUsername, user?.uid);
        if (!validation.valid) {
          setUsernameError(validation.error || 'Invalid username');
          if (validation.suggestions) {
            setUsernameSuggestions(validation.suggestions);
          }
        }
      } catch (error) {
      } finally {
        setValidatingUsername(false);
      }
    }
  };

  const handleSaveProfile = async () => {
    // Validate username before saving
    if (username !== userProfile?.username) {
      setValidatingUsername(true);
      const validation = await validateUsername(username, user?.uid);
      setValidatingUsername(false);

      if (!validation.valid) {
        setUsernameError(validation.error || 'Invalid username');
        if (validation.suggestions) {
          setUsernameSuggestions(validation.suggestions);
        }
        toast.error('Please fix the username error before saving');
        return;
      }
    }

    setSaving(true);
    try {
      await updateProfile({
        username,
        displayName,
        bio,
        discordUsername,
      });
      toast.success('Profile updated successfully!');
      setUsernameError('');
      setUsernameSuggestions([]);
    } catch (error: any) {
      if (error.suggestions) {
        setUsernameError(error.message);
        setUsernameSuggestions(error.suggestions);
      } else {
        toast.error(error.message || 'Failed to update profile');
      }
    } finally {
      setSaving(false);
    }
  };

  const handlePrivacyUpdate = async (updates: Partial<UserProfile>) => {
    try {
      await updateProfile(updates);
      toast.success('Privacy settings updated!');
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  const handlePreferenceUpdate = async (updates: Partial<UserProfile>) => {
    try {
      await updateProfile(updates);
      toast.success('Preferences updated!');
    } catch (error) {
      toast.error('Failed to update preferences');
    }
  };

  const handleNotificationUpdate = async (key: string, value: boolean) => {
    try {
      await updateProfile({
        emailNotifications: {
          ...userProfile.emailNotifications,
          [key]: value,
        },
      });
      toast.success('Notification settings updated!');
    } catch (error) {
      toast.error('Failed to update notifications');
    }
  };

  const handlePasswordReset = async () => {
    if (!user?.email) {
      toast.error('No email address found');
      return;
    }

    setSendingPasswordReset(true);
    try {
      const response = await fetch('/api/auth/send-password-reset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: user.email }),
      });

      const data = await response.json();

      if (data.success) {
        toast.success(`Password reset email sent to ${user.email}. Please check your inbox.`, {
          duration: 5000,
        });
      } else {
        toast.error(data.error || 'Failed to send password reset email');
      }
    } catch (error) {
      toast.error('Failed to send password reset email');
    } finally {
      setSendingPasswordReset(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmation !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }

    setDeleting(true);
    try {
      const { updateDoc, doc } = await import('firebase/firestore');
      const { signOut } = await import('firebase/auth');
      const { db, auth } = await import('@/lib/firebase');

      if (!user) {
        throw new Error('No user logged in');
      }

      // Set deletion date 30 days from now (soft delete)
      const deletionDate = new Date();
      deletionDate.setDate(deletionDate.getDate() + 30);

      // Mark account for deletion instead of immediate deletion
      await updateDoc(doc(db, 'users', user.uid), {
        scheduledForDeletion: true,
        deletionDate: deletionDate.toISOString(),
        deletionRequestedAt: new Date().toISOString(),
      });

      // Sign out the user
      await signOut(auth);

      toast.success('Account scheduled for deletion in 30 days. You can recover it by signing in again within this period.');
      router.push('/');
    } catch (error: any) {
      toast.error(error.message || 'Failed to schedule account deletion. Please try again.');
      setDeleting(false);
    }
  };

  const handlePWAInstall = async () => {
    if (isStandalone) {
      toast.info('App is already installed!');
      return;
    }

    if (isIOS) {
      toast.info('Tap the Share button and select "Add to Home Screen"', {
        duration: 5000,
      });
      return;
    }

    // Clear any dismissed flags first to allow reinstall
    clearDismissedFlag();

    const success = await triggerInstall();
    if (success) {
      toast.success('App installed successfully!');
    } else {
      toast.error('Installation cancelled or unavailable');
    }
  };

  const handleLinkGoogle = async () => {
    if (!user) {
      toast.error('No user logged in');
      return;
    }

    setLinkingGoogle(true);
    try {
      const response = await fetch('/api/auth/request-google-link', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.uid }),
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Link confirmation email sent. Please check your inbox.', {
          duration: 5000,
        });
      } else {
        toast.error(data.error || 'Failed to send link email');
      }
    } catch (error) {
      toast.error('Failed to send link email');
    } finally {
      setLinkingGoogle(false);
    }
  };

  // Check if user has Google linked
  const hasGoogleLinked = user?.providerData?.some(
    (provider) => provider.providerId === 'google.com'
  );

  return (
    <div className="min-h-screen bg-black">
      <Header showSearch={false} transparent={true} page="dashboard" />

      <main className="pt-20 md:pt-24 pb-12">
        <div className="container mx-auto px-4 max-w-5xl">
          {/* Header */}
          <div className="mb-6 md:mb-8">
            <h1 className="text-2xl md:text-4xl font-bold text-white mb-1 md:mb-2">Settings</h1>
            <p className="text-sm md:text-base text-gray-400">Manage your account and preferences</p>
          </div>

          {/* Settings Tabs */}
          <Tabs defaultValue="account" className="space-y-6">
            <TabsList className="bg-gray-900/80 border-2 border-gray-700/50 p-1.5 md:p-1 h-auto">
              <TabsTrigger
                value="account"
                className="data-[state=active]:bg-red-600 data-[state=active]:text-white px-4 py-3 md:py-2"
              >
                <User className="h-5 w-5 md:h-4 md:w-4 md:mr-2" />
                <span className="hidden md:inline">Account</span>
              </TabsTrigger>
              {/* Hidden for now - will implement later
              <TabsTrigger
                value="connections"
                className="data-[state=active]:bg-red-600 data-[state=active]:text-white px-4 py-3 md:py-2"
              >
                <LinkIcon className="h-5 w-5 md:h-4 md:w-4 md:mr-2" />
                <span className="hidden md:inline">Connections</span>
              </TabsTrigger>
              */}
              <TabsTrigger
                value="privacy"
                className="data-[state=active]:bg-red-600 data-[state=active]:text-white px-4 py-3 md:py-2"
              >
                <Shield className="h-5 w-5 md:h-4 md:w-4 md:mr-2" />
                <span className="hidden md:inline">Privacy</span>
              </TabsTrigger>
              <TabsTrigger
                value="notifications"
                className="data-[state=active]:bg-red-600 data-[state=active]:text-white px-4 py-3 md:py-2"
              >
                <Bell className="h-5 w-5 md:h-4 md:w-4 md:mr-2" />
                <span className="hidden md:inline">Notifications</span>
              </TabsTrigger>
              <TabsTrigger
                value="social"
                className="data-[state=active]:bg-red-600 data-[state=active]:text-white px-4 py-3 md:py-2"
              >
                <Users className="h-5 w-5 md:h-4 md:w-4 md:mr-2" />
                <span className="hidden md:inline">Social</span>
              </TabsTrigger>
            </TabsList>

            {/* Account Settings */}
            <TabsContent value="account" className="space-y-6">
              <SettingsSection
                title="Profile Information"
                description="Update your profile details"
                icon={<User className="h-6 w-6 text-red-400" />}
              >
                <SettingItem label="Profile Picture" description="Upload a profile picture or use your Google profile photo">
                  <ProfilePictureUpload />
                </SettingItem>

                <SettingItem label="Username" description="Your unique username (3-20 characters, letters, numbers, _ and -)">
                  <div className="w-full max-w-md space-y-2">
                    <div className="relative">
                      <Input
                        value={username}
                        onChange={(e) => handleUsernameChange(e.target.value)}
                        className={`bg-black/50 border-gray-700 text-white ${
                          usernameError ? 'border-red-500' : validatingUsername ? 'border-yellow-500' : ''
                        }`}
                        placeholder="username"
                      />
                      {validatingUsername && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          <div className="animate-spin h-4 w-4 border-2 border-red-500 border-t-transparent rounded-full"></div>
                        </div>
                      )}
                    </div>

                    {usernameError && (
                      <div className="text-sm text-red-400">{usernameError}</div>
                    )}

                    {usernameSuggestions.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-sm text-gray-400">Suggestions:</p>
                        <div className="flex flex-wrap gap-2">
                          {usernameSuggestions.map((suggestion) => (
                            <button
                              key={suggestion}
                              onClick={() => {
                                setUsername(suggestion);
                                setUsernameError('');
                                setUsernameSuggestions([]);
                              }}
                              className="px-3 py-1 text-sm bg-gray-800 hover:bg-red-600 text-white rounded-md transition-colors"
                            >
                              {suggestion}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </SettingItem>

                <SettingItem label="Display Name" description="Name shown on your profile">
                  <Input
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    className="w-full sm:w-64 bg-black/50 border-gray-700 text-white"
                    placeholder="Display name"
                  />
                </SettingItem>

                <SettingItem label="Bio" description="Tell others about yourself (200 characters)">
                  <Textarea
                    value={bio}
                    onChange={(e) => setBio(e.target.value.slice(0, 200))}
                    className="w-full bg-black/50 border-gray-700 text-white"
                    placeholder="I love watching..."
                    maxLength={200}
                    rows={3}
                  />
                </SettingItem>

                <SettingItem label="Email" description="Your account email">
                  <Input
                    value={user.email || ''}
                    disabled
                    className="w-full sm:w-64 bg-gray-800/50 border-gray-700 text-gray-400"
                  />
                </SettingItem>

                <div className="flex justify-end pt-4">
                  <Button
                    onClick={handleSaveProfile}
                    disabled={saving}
                    className="w-full sm:w-auto bg-red-600 hover:bg-red-700"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </SettingsSection>

              <SettingsSection
                title="Security"
                description="Manage your password and security settings"
                icon={<Lock className="h-6 w-6 text-red-400" />}
              >
                <SettingItem
                  label="Google Account"
                  description={
                    hasGoogleLinked
                      ? "Your Google account is linked for easier sign-in"
                      : "Link your Google account for easier sign-in (requires email confirmation)"
                  }
                >
                  {hasGoogleLinked ? (
                    <div className="flex items-center gap-2 text-green-400">
                      <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                      <span className="text-sm font-medium">Linked</span>
                    </div>
                  ) : (
                    <Button
                      onClick={handleLinkGoogle}
                      disabled={linkingGoogle}
                      variant="outline"
                      className="w-full sm:w-auto border-gray-600 text-gray-300 hover:bg-gray-800"
                    >
                      <LinkIcon className="h-4 w-4 mr-2" />
                      {linkingGoogle ? 'Linking...' : 'Link Google Account'}
                    </Button>
                  )}
                </SettingItem>

                <SettingItem
                  label="Password"
                  description="We'll send a reset link to your email. You must use a new password (not your current one)."
                >
                  <Button
                    variant="outline"
                    className="w-full sm:w-auto border-gray-600 text-gray-300"
                    onClick={handlePasswordReset}
                    disabled={sendingPasswordReset}
                  >
                    {sendingPasswordReset ? 'Sending Email...' : 'Send Reset Link'}
                  </Button>
                </SettingItem>
              </SettingsSection>

              <SettingsSection
                title="Mobile App"
                description="Install Epify as a mobile application"
                icon={<Smartphone className="h-6 w-6 text-red-400" />}
              >
                <SettingItem
                  label={isStandalone ? "App Installed" : "Install App"}
                  description={
                    isStandalone
                      ? "You're using the installed app"
                      : isIOS
                      ? "Install this app on your home screen for quick access"
                      : "Get a native app experience with offline support"
                  }
                >
                  <div className="flex items-center gap-3">
                    {isStandalone ? (
                      <div className="flex items-center gap-2 text-green-400">
                        <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                        <span className="text-sm font-medium">Installed</span>
                      </div>
                    ) : (
                      <Button
                        onClick={handlePWAInstall}
                        className="w-full sm:w-auto bg-red-600 hover:bg-red-700"
                        disabled={!isInstallable}
                      >
                        <Download className="h-4 w-4 mr-2" />
                        {isIOS ? 'Install Instructions' : 'Install App'}
                      </Button>
                    )}
                  </div>
                </SettingItem>

                {!isStandalone && (
                  <div className="mt-4 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <p className="text-sm text-red-300">
                      <strong>Benefits of Installing:</strong> Get offline access, faster loading,
                      push notifications for new episodes, and a native app experience without using browser storage.
                    </p>
                  </div>
                )}
              </SettingsSection>

              <SettingsSection
                title="Danger Zone"
                description="Irreversible actions"
                icon={<AlertTriangle className="h-6 w-6 text-red-400" />}
              >
                <SettingItem
                  label="Delete Account"
                  description="Permanently delete your account and all data"
                >
                  <Button
                    variant="destructive"
                    className="w-full sm:w-auto bg-red-600 hover:bg-red-700"
                    onClick={() => setShowDeleteDialog(true)}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Account
                  </Button>
                </SettingItem>
              </SettingsSection>
            </TabsContent>

            {/* Delete Account Confirmation Dialog */}
            <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
              <DialogContent className="bg-gray-900 border-gray-800">
                <DialogHeader>
                  <DialogTitle className="text-white flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-red-500" />
                    Delete Account
                  </DialogTitle>
                  <DialogDescription className="text-gray-400">
                    Your account will be scheduled for deletion in 30 days. You can recover your account by signing in again within this period.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                      <p className="text-sm text-blue-300">
                        <strong>30-Day Grace Period:</strong> Your account and data will be preserved for 30 days. Sign in anytime during this period to cancel the deletion.
                      </p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm text-gray-300">
                      After 30 days, the following data will be permanently deleted:
                    </p>
                    <ul className="list-disc list-inside text-sm text-gray-400 space-y-1 ml-2">
                      <li>Your profile information</li>
                      <li>Your entire watchlist ({watchlist?.length || 0} items)</li>
                      <li>All your ratings and reviews</li>
                      <li>Your disliked items</li>
                      <li>All account settings and preferences</li>
                    </ul>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm text-gray-300">
                      Type <span className="font-bold text-red-400">DELETE</span> to confirm:
                    </label>
                    <Input
                      value={deleteConfirmation}
                      onChange={(e) => setDeleteConfirmation(e.target.value)}
                      className="bg-black/50 border-gray-700 text-white"
                      placeholder="DELETE"
                      disabled={deleting}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowDeleteDialog(false);
                      setDeleteConfirmation('');
                    }}
                    disabled={deleting}
                    className="border-gray-600 text-gray-300"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={handleDeleteAccount}
                    disabled={deleteConfirmation !== 'DELETE' || deleting}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    {deleting ? (
                      <>
                        <span className="animate-spin mr-2">⏳</span>
                        Deleting...
                      </>
                    ) : (
                      <>
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete Account
                      </>
                    )}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Connections Tab - Hidden for now, will implement later
            <TabsContent value="connections" className="space-y-6">
              <SettingsSection
                title="Streaming Services"
                description="Connect your streaming platforms to sync watchlists and get recommendations"
                icon={<LinkIcon className="h-6 w-6 text-red-400" />}
              >
                <div className="space-y-4">
                  <SettingItem
                    label="Netflix"
                    description="Connect your Netflix account"
                  >
                    <Button variant="outline" className="w-full sm:w-auto border-gray-600 text-gray-300">
                      <LinkIcon className="h-4 w-4 mr-2" />
                      Connect Netflix
                    </Button>
                  </SettingItem>

                  <SettingItem
                    label="Hulu"
                    description="Connect your Hulu account"
                  >
                    <Button variant="outline" className="w-full sm:w-auto border-gray-600 text-gray-300">
                      <LinkIcon className="h-4 w-4 mr-2" />
                      Connect Hulu
                    </Button>
                  </SettingItem>

                  <SettingItem
                    label="Paramount+"
                    description="Connect your Paramount+ account"
                  >
                    <Button variant="outline" className="w-full sm:w-auto border-gray-600 text-gray-300">
                      <LinkIcon className="h-4 w-4 mr-2" />
                      Connect Paramount+
                    </Button>
                  </SettingItem>

                  <SettingItem
                    label="Peacock"
                    description="Connect your Peacock account"
                  >
                    <Button variant="outline" className="w-full sm:w-auto border-gray-600 text-gray-300">
                      <LinkIcon className="h-4 w-4 mr-2" />
                      Connect Peacock
                    </Button>
                  </SettingItem>

                  <SettingItem
                    label="Disney+"
                    description="Connect your Disney+ account"
                  >
                    <Button variant="outline" className="w-full sm:w-auto border-gray-600 text-gray-300">
                      <LinkIcon className="h-4 w-4 mr-2" />
                      Connect Disney+
                    </Button>
                  </SettingItem>

                  <SettingItem
                    label="HBO Max"
                    description="Connect your HBO Max account"
                  >
                    <Button variant="outline" className="w-full sm:w-auto border-gray-600 text-gray-300">
                      <LinkIcon className="h-4 w-4 mr-2" />
                      Connect HBO Max
                    </Button>
                  </SettingItem>

                  <SettingItem
                    label="Amazon Prime Video"
                    description="Connect your Prime Video account"
                  >
                    <Button variant="outline" className="w-full sm:w-auto border-gray-600 text-gray-300">
                      <LinkIcon className="h-4 w-4 mr-2" />
                      Connect Prime Video
                    </Button>
                  </SettingItem>
                </div>

                <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                  <p className="text-sm text-blue-300">
                    <strong>Coming Soon:</strong> Connect your streaming accounts to automatically sync your watchlists,
                    get personalized recommendations based on what you already watch, and discover content across all your services.
                  </p>
                </div>
              </SettingsSection>

              <SettingsSection
                title="Social Platforms"
                description="Connect with social platforms for enhanced features"
                icon={<MessageSquare className="h-6 w-6 text-red-400" />}
              >
                <SettingItem
                  label="Discord"
                  description="Connect Discord for watch parties and community features"
                >
                  <Button variant="outline" className="w-full sm:w-auto border-gray-600 text-gray-300">
                    <MessageSquare className="h-4 w-4 mr-2" />
                    Connect Discord
                  </Button>
                </SettingItem>

                <div className="mt-6 p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                  <p className="text-sm text-purple-300">
                    <strong>Discord Integration:</strong> Once connected, you'll be able to organize watch parties,
                    share your watchlist with friends, and get notifications when your friends start watching shows you might like.
                  </p>
                </div>
              </SettingsSection>
            </TabsContent>
            */}

            {/* Privacy Settings */}
            <TabsContent value="privacy" className="space-y-6">
              <SettingsSection
                title="Profile Visibility"
                description="Control who can see your profile"
                icon={<Shield className="h-6 w-6 text-red-400" />}
              >
                <SettingItem
                  label="Profile Visibility"
                  description="Who can view your profile"
                >
                  <Select
                    value={userProfile.profileVisibility}
                    onValueChange={(value: 'public' | 'private' | 'friends') =>
                      handlePrivacyUpdate({ profileVisibility: value })
                    }
                  >
                    <SelectTrigger className="w-full sm:w-48 bg-black/50 border-gray-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-900 border-gray-700">
                      <SelectItem value="public">Public</SelectItem>
                      <SelectItem value="private">Private</SelectItem>
                      <SelectItem value="friends">Friends Only</SelectItem>
                    </SelectContent>
                  </Select>
                </SettingItem>

                <SettingItem
                  label="Show Email"
                  description="Display your email on your profile"
                >
                  <Switch
                    checked={userProfile.showEmail}
                    onCheckedChange={(checked) =>
                      handlePrivacyUpdate({ showEmail: checked })
                    }
                  />
                </SettingItem>

                <SettingItem
                  label="Show Stats"
                  description="Display watchlist statistics"
                >
                  <Switch
                    checked={userProfile.showWatchlistStats}
                    onCheckedChange={(checked) =>
                      handlePrivacyUpdate({ showWatchlistStats: checked })
                    }
                  />
                </SettingItem>

                <SettingItem
                  label="Show Recent Activity"
                  description="Display recently watched items"
                >
                  <Switch
                    checked={userProfile.showRecentActivity}
                    onCheckedChange={(checked) =>
                      handlePrivacyUpdate({ showRecentActivity: checked })
                    }
                  />
                </SettingItem>

                <SettingItem
                  label="Show Ratings"
                  description="Display your ratings publicly"
                >
                  <Switch
                    checked={userProfile.showRatings}
                    onCheckedChange={(checked) =>
                      handlePrivacyUpdate({ showRatings: checked })
                    }
                  />
                </SettingItem>
              </SettingsSection>
            </TabsContent>

            {/* Notification Settings */}
            <TabsContent value="notifications" className="space-y-6">
              <SettingsSection
                title="Email Notifications"
                description="Choose what emails you receive"
                icon={<Bell className="h-6 w-6 text-red-400" />}
              >
                <SettingItem
                  label="New Seasons"
                  description="Notify when a new season is available"
                >
                  <Switch
                    checked={userProfile.emailNotifications?.newSeasons ?? true}
                    onCheckedChange={(checked) =>
                      handleNotificationUpdate('newSeasons', checked)
                    }
                  />
                </SettingItem>

                <SettingItem
                  label="New Episodes"
                  description="Notify when new episodes air"
                >
                  <Switch
                    checked={userProfile.emailNotifications?.newEpisodes ?? true}
                    onCheckedChange={(checked) =>
                      handleNotificationUpdate('newEpisodes', checked)
                    }
                  />
                </SettingItem>
              </SettingsSection>
            </TabsContent>

            {/* Social Settings */}
            <TabsContent value="social" className="space-y-6">
              <SettingsSection
                title="Community Features"
                description="Manage your social and community preferences"
                icon={<Users className="h-6 w-6 text-red-400" />}
              >
                <SettingItem
                  label="Allow Friend Requests"
                  description="Let others send you friend requests"
                >
                  <Switch
                    checked={userProfile.allowFriendRequests ?? true}
                    onCheckedChange={(checked) =>
                      handlePreferenceUpdate({ allowFriendRequests: checked })
                    }
                  />
                </SettingItem>

                <SettingItem
                  label="Accept Recommendations"
                  description="Receive show recommendations from friends"
                >
                  <Switch
                    checked={userProfile.acceptRecommendations ?? true}
                    onCheckedChange={(checked) =>
                      handlePreferenceUpdate({ acceptRecommendations: checked })
                    }
                  />
                </SettingItem>

                <SettingItem
                  label="Share Watchlist Updates"
                  description="Let friends see when you add or watch shows"
                >
                  <Switch
                    checked={userProfile.shareWatchlistUpdates ?? false}
                    onCheckedChange={(checked) =>
                      handlePreferenceUpdate({ shareWatchlistUpdates: checked })
                    }
                  />
                </SettingItem>
              </SettingsSection>

              <div className="p-4 bg-gradient-to-r from-purple-500/10 to-blue-500/10 border border-purple-500/30 rounded-lg">
                <p className="text-sm text-gray-300">
                  <strong className="text-purple-400">Social Features Coming Soon:</strong> Friend system, shared watchlists,
                  watch party scheduling, recommendations from friends, and activity feeds. Stay tuned!
                </p>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
}
